import {useState, useMemo, useEffect} from 'react'
import {useSearchParams} from 'react-router-dom'
import {ListChecks, Trash2} from 'lucide-react'
import {useApp} from '../context/AppContext'
import type {Priority} from '../types'
import type {SortField, SortDirection} from '../components/tasks/TasksTable'
import TasksStats from '../components/tasks/TasksStats'
import TasksFilters, {defaultFilters, type TaskFilters} from '../components/tasks/TasksFilters'
import TasksTable from '../components/tasks/TasksTable'
import {matchesTaskFilters} from '../components/board/taskFilters'
import ConfirmDialog from '../components/common/ConfirmDialog'
import PageHeader from '../components/common/PageHeader'

import {PageTitle} from '../../../../_metronic/layout/core'

const priorityOrder: Record<Priority, number> = {critical: 0, high: 1, medium: 2, low: 3}

export default function TasksPage() {
  const {state, apiDeleteTask, getBoardById} = useApp()

  const [searchParams, setSearchParams] = useSearchParams()
  const attention = searchParams.get('attention')
  const boardFromQuery = searchParams.get('board')
  const [bulkError, setBulkError] = useState('')
  const [pageSize, setPageSize] = useState(25)
  const [filters, setFilters] = useState<TaskFilters>(defaultFilters)
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showBulkDelete, setShowBulkDelete] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const ITEMS_PER_PAGE = pageSize

  useEffect(() => {
    if (!attention && !boardFromQuery) return
    const next = {
      ...defaultFilters,
      pendingOnly: ['mine', 'unassigned', 'today', 'overdue'].includes(attention || ''),
    }
    if (attention === 'mine' || attention === 'unassigned') next.assignment = attention
    else if (attention === 'overdue' || attention === 'today') next.dueDateRange = attention
    if (boardFromQuery) next.boardIds = [boardFromQuery]
    setFilters(next)
    setCurrentPage(1)
    setSelectedIds(new Set())
  }, [attention, boardFromQuery])

  useEffect(() => {
    setSelectedIds((previous) => {
      const ids = new Set(state.tasks.map((task) => task.id))
      const remaining = new Set([...previous].filter((id) => ids.has(id)))
      return remaining.size === previous.size ? previous : remaining
    })
  }, [state.tasks])

  const allStatuses = useMemo(() => {
    const set = new Set<string>()
    state.boards.forEach((b) => b.columns.forEach((c) => set.add(c.status)))
    state.tasks.forEach((task) => set.add(task.status))
    return Array.from(set)
  }, [state.boards, state.tasks])

  const filtered = useMemo(() => {
    return state.tasks.filter((task) => {
      if (!matchesTaskFilters(task, filters, state.currentUser?.id)) return false
      if (filters.boardIds.length > 0 && !filters.boardIds.includes(task.boardId)) return false
      if (filters.hasSubtasks === 'yes' && task.subtasks.length === 0) return false
      if (filters.hasSubtasks === 'no' && task.subtasks.length > 0) return false

      return true
    })
  }, [state.tasks, filters, state.currentUser?.id])

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'title':
          cmp = a.title.localeCompare(b.title)
          break
        case 'priority':
          cmp = priorityOrder[a.priority] - priorityOrder[b.priority]
          break
        case 'status':
          cmp = a.status.localeCompare(b.status)
          break
        case 'dueDate':
          if (!a.dueDate && b.dueDate) return 1
          if (a.dueDate && !b.dueDate) return -1
          cmp = (a.dueDate || '9999').localeCompare(b.dueDate || '9999')
          break
        case 'storyPoints':
          cmp = a.storyPoints - b.storyPoints
          break
        case 'createdAt':
          cmp = a.createdAt.localeCompare(b.createdAt)
          break
        case 'board': {
          const boardA = getBoardById(a.boardId)?.name || ''
          const boardB = getBoardById(b.boardId)?.name || ''
          cmp = boardA.localeCompare(boardB)
          break
        }
      }
      return sortDirection === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortField, sortDirection, getBoardById])

  const totalPages = Math.max(1, Math.ceil(sorted.length / ITEMS_PER_PAGE))
  const visiblePage = Math.min(currentPage, totalPages)
  const paginatedTasks = sorted.slice(
    (visiblePage - 1) * ITEMS_PER_PAGE,
    visiblePage * ITEMS_PER_PAGE
  )

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleToggleSelectAll = () => {
    setSelectedIds((previous) => {
      const next = new Set(previous)
      const allVisibleSelected = paginatedTasks.every((task) => previous.has(task.id))
      paginatedTasks.forEach((task) =>
        allVisibleSelected ? next.delete(task.id) : next.add(task.id)
      )
      return next
    })
  }

  const handleBulkDelete = async () => {
    if (bulkDeleting) return
    setBulkDeleting(true)
    setBulkError('')
    try {
      const ids = Array.from(selectedIds)
      const results = await Promise.allSettled(ids.map((id) => apiDeleteTask(id)))
      const failed = ids.filter((_, index) => results[index].status === 'rejected')
      setSelectedIds(new Set(failed))
      if (failed.length)
        setBulkError(
          `${failed.length} tarefa(s) não puderam ser excluídas. Elas continuam selecionadas para uma nova tentativa.`
        )
    } finally {
      setBulkDeleting(false)
      setShowBulkDelete(false)
    }
  }

  const handleFiltersChange = (next: TaskFilters) => {
    setFilters(next)
    setCurrentPage(1)
    setSelectedIds(new Set())
    setBulkError('')
    if (searchParams.has('attention') || searchParams.has('board')) {
      const params = new URLSearchParams(searchParams)
      params.delete('attention')
      params.delete('board')
      setSearchParams(params, {replace: true})
    }
  }

  return (
    <div className='devops-page space-y-5'>
      <PageTitle>DevOps - Tarefas</PageTitle>
      <PageHeader
        eyebrow='Portfólio'
        icon={ListChecks}
        title='Todas as tarefas'
        description='Consulte, filtre e organize o trabalho de todos os quadros sem perder o contexto das entregas.'
        meta={
          <>
            <span>
              {filtered.length} {filtered.length === 1 ? 'resultado' : 'resultados'}
            </span>
            {filtered.length !== state.tasks.length && (
              <span>{state.tasks.length} tarefas no total</span>
            )}
          </>
        }
      />

      <TasksStats tasks={filtered} />

      <TasksFilters filters={filters} onChange={handleFiltersChange} allStatuses={allStatuses} />

      {bulkError && (
        <p
          role='alert'
          className='rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'
        >
          {bulkError}
        </p>
      )}

      {selectedIds.size > 0 && (
        <div className='devops-surface flex flex-wrap items-center gap-3 bg-brand-50/80 px-4 py-3 animate-fade-in'>
          <span className='text-sm font-medium text-brand-700'>
            {selectedIds.size}{' '}
            {selectedIds.size === 1 ? 'tarefa selecionada' : 'tarefas selecionadas'}
          </span>
          <div className='flex-1' />
          <button
            disabled={bulkDeleting}
            onClick={() => setShowBulkDelete(true)}
            className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors'
          >
            <Trash2 size={14} />
            Excluir
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors'
          >
            Cancelar
          </button>
        </div>
      )}

      <TasksTable
        tasks={paginatedTasks}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
        hasFilters={JSON.stringify(filters) !== JSON.stringify(defaultFilters)}
        onClearFilters={() => handleFiltersChange(defaultFilters)}
      />

      {sorted.length > 0 && (
        <div className='devops-surface flex flex-wrap items-center justify-between gap-3 px-4 py-3'>
          <p className='text-sm text-slate-500'>
            Mostrando {(visiblePage - 1) * ITEMS_PER_PAGE + 1} -{' '}
            {Math.min(visiblePage * ITEMS_PER_PAGE, sorted.length)} de {sorted.length}
          </p>
          <div className='flex flex-wrap items-center gap-2'>
            <label className='flex items-center gap-2 text-xs text-slate-500'>
              Por página
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className='rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700'
              >
                {[25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <button
              onClick={() => setCurrentPage(Math.max(1, visiblePage - 1))}
              disabled={visiblePage === 1}
              className='px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
            >
              Anterior
            </button>
            {Array.from({length: totalPages}, (_, i) => i + 1)
              .filter((page) => {
                if (totalPages <= 7) return true
                if (page === 1 || page === totalPages) return true
                if (Math.abs(page - visiblePage) <= 1) return true
                return false
              })
              .map((page, idx, arr) => {
                const showEllipsis = idx > 0 && page - arr[idx - 1] > 1
                return (
                  <span key={page} className='flex items-center'>
                    {showEllipsis && <span className='px-1 text-slate-400'>...</span>}
                    <button
                      onClick={() => setCurrentPage(page)}
                      aria-label={`Página ${page}`}
                      aria-current={page === visiblePage ? 'page' : undefined}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        page === visiblePage
                          ? 'bg-brand-500 text-white'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  </span>
                )
              })}
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, visiblePage + 1))}
              disabled={visiblePage === totalPages}
              className='px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showBulkDelete}
        title='Excluir tarefas'
        message={`Tem certeza que deseja excluir ${selectedIds.size} tarefa${
          selectedIds.size > 1 ? 's' : ''
        }? Esta ação não pode ser desfeita.`}
        confirmLabel={bulkDeleting ? 'Excluindo...' : 'Excluir'}
        onConfirm={handleBulkDelete}
        onCancel={() => {
          if (!bulkDeleting) setShowBulkDelete(false)
        }}
      />
    </div>
  )
}
